import { StoryblokComponent, storyblokEditable } from 'gatsby-source-storyblok';
import React, { memo, useContext, useEffect, useState } from 'react';
import { LayoutContext } from '../../../contexts/LayoutContext';
import { PageContext } from '../../../contexts/PageContext';
import { Blok } from '../../../interfaces/Blok.interface';
import QuoteStageMainLayout from '../QuoteStageMainLayout/QuoteStageMainLayout';
import { QuoteStageContext } from '../../../contexts/QuoteStageContext';
import { getQueryParam } from '../../../utils/getQueryParam/getQueryParam.util';
import { StoryblokPartnerSite } from '../../../interfaces/StoryblokPartnerSite.interface';
import { StoryblokNode } from '../../../interfaces/StoryblokNode.interface';
import QuoteFormHeadingBar from '../../QuoteFormHeadingBar/QuoteFormHeadingBar';
import Grid from '../../Utils/Grid/Grid';
import Col from '../../Utils/Col/Col';
import Heading, { HeadingTypes } from '../../Heading/Heading';
import { Theme } from '../../../interfaces/enums/Theme.enum';
import { withBlokWrapper } from '../../BlokWrapper/BlokWrapper';
import { QuoteContext } from '../../../contexts/Quote';
import { navigate } from 'gatsby';
import { useGetQuote } from '../../../hooks/useGetQuote';
import { useGetCostBreakdown } from '../../../hooks/useGetCostBreakdown';
import TestingGridMolecule from '../../TestingGrid/TestingGrid.molecule';
import { postQuoteOverviewToDataLayer } from '../../../utils/postQuoteOverviewToDataLayer/postQuoteOverviewToDataLayer.util';

interface PartnerPageContent extends Blok {
  partner: any[];
  page_content: any[];
  default_content: boolean;
}
interface QuoteConfirmationPageBlokProps extends Blok {
  heading: string;
  left_column: PartnerPageContent[];
  right_column: Blok[];
  show_default_content: boolean;
}

interface QuoteConfirmationPageProps {
  blok: QuoteConfirmationPageBlokProps;
}

const QuoteConfirmationPage: React.FC<QuoteConfirmationPageProps> = ({
  blok,
}) => {
  const { show_default_content, heading, left_column, right_column } = blok;

  const { header, footer } = useContext(LayoutContext);

  let searchParams: URLSearchParams = new URLSearchParams();
  const [quoteParams, setQuoteParams] = useState({
    quoteReference: '',
    trackingRef: '',
  });

  const [hasErrors, setHasErrors] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    searchParams = new URLSearchParams(window.location.search);

    if (!searchParams.get('quotation') && !searchParams.get('TrackingRef')) {
      navigate('/instant-quote/quote-stage-one/');
    }

    setQuoteParams({
      quoteReference: searchParams.get('quotation') || '',
      trackingRef: searchParams.get('TrackingRef') || '',
    });
  }, []);

  const applicablePartnerBloks = left_column.find((blok) =>
    blok?.partner?.find(
      (partner) =>
        partner?.content?.api_id === quoteParams.quoteReference?.substring(0, 7)
    )
  );

  const defaultContent = left_column.find((blok) => blok?.default_content);

  const isOldSetup = !left_column?.find(
    (blok) => blok?.component === 'partner_page_content'
  );

  const [quote, quoteErrors, quoteIsLoading] = useGetQuote(
    quoteParams.quoteReference,
    quoteParams.trackingRef
  );

  const [costBreakdown, costBreakdownErrors, costBreakdownIsLoading] =
    useGetCostBreakdown(quoteParams.quoteReference, quoteParams.trackingRef);

  useEffect(() => {
    if (quote && costBreakdown) {
      setIsLoading(false);
    }
  }, [quote, costBreakdown]);

  useEffect(() => {
    setHasErrors(quoteErrors || costBreakdownErrors);
  }, [quoteErrors, costBreakdownErrors]);

  useEffect(() => {
    if (quote && costBreakdown) {
      handlePostQuoteOverviewToDataLater('quote_step4_success');
    }
  }, [quote && costBreakdown]);

  const handlePostQuoteOverviewToDataLater = (eventName: string): void => {
    postQuoteOverviewToDataLayer(
      eventName,
      quoteParams.quoteReference!,
      quoteParams.trackingRef!,
      costBreakdown!
    );
  };

  return (
    <QuoteContext.Provider
      value={{
        quoteDetails: quote,
        quoteCostBreakdown: costBreakdown,
        quoteReference: quoteParams.quoteReference,
        trackingRef: quoteParams.quoteReference,
      }}
    >
      <PageContext.Provider
        value={{
          headerIsTransparent: false,
          isHeaderSearchFunctionalityRequired: true,
          showContactBar: false,
        }}
      >
        <div className="flex flex-col justify-between">
          {header && (
            <StoryblokComponent blok={header.content} key={header._uid} />
          )}

          <main className="mb-auto" {...storyblokEditable(blok)}>
            <section className="py-20 container mx-auto">
              {heading && (
                <Heading text={heading} headingType={HeadingTypes.h1} />
              )}
              <Grid gapY="gap-y-0">
                <Col
                  span={{ xl: 6, xxl: 5 }}
                  start={{ xxl: 2 }}
                  order={{ default: 2, xl: 1 }}
                >
                  <div>
                    {isOldSetup
                      ? left_column?.map((blok: Blok) => (
                          <StoryblokComponent blok={blok} key={blok._uid} />
                        ))
                      : applicablePartnerBloks
                      ? applicablePartnerBloks?.page_content?.map(
                          (blok: Blok) => (
                            <StoryblokComponent blok={blok} key={blok._uid} />
                          )
                        )
                      : defaultContent?.page_content?.map((blok: Blok) => (
                          <StoryblokComponent blok={blok} key={blok._uid} />
                        ))}
                  </div>
                </Col>
                <Col
                  span={{ xl: 5, xxl: 4 }}
                  start={{ xl: 8, xxl: 8 }}
                  order={{ default: 1, xl: 2 }}
                >
                  <div className="xl:space-y-8">
                    {right_column.map((blok: Blok) => (
                      <StoryblokComponent blok={blok} key={blok._uid} />
                    ))}
                  </div>
                </Col>
              </Grid>
            </section>
          </main>

          {footer && (
            <StoryblokComponent blok={footer.content} key={footer._uid} />
          )}
        </div>
        <TestingGridMolecule />
      </PageContext.Provider>
    </QuoteContext.Provider>
  );
};

export default memo(withBlokWrapper(QuoteConfirmationPage, Theme.ALT));
